/**
 * The Configuration tab: one service's environment variables and secrets (vfs-service src/appconfig). The scheduled
 * jobs are a tab of their own, Operate › Jobs (JobsPanel): a line here links to it (`onOpenJobs`).
 *
 * - Variables live in `services/<svc>/.flow/env.json` (in git, so in every copy and delivery of the project). Editors
 *   see and change their values; a viewer sees their names only.
 * - Secrets are write-only: Set or Replace, never shown again (not even to the person who saved them), never in git.
 *   Their names are declared in env.json, so a delivery knows they must be provided. A secret is a value or a file
 *   (FileSecretForm: a service account key, a certificate): the service gets a file's path in its variable.
 * - A change reaches a running service when it restarts: "Restart to apply" (the runner gives a service its
 *   configuration at every start).
 *
 * `focus`: `{ service }` opens that service (Home's service row › Configuration). The old `{ section: 'jobs' }` is the
 * Jobs tab now (workspaceModel normalizeView).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { TrashIcon } from '@phosphor-icons/react';
import DataTable from '../table/DataTable';
import { IconButton } from '../table/TableParts';
import { deleteSecret, deleteVariable, getAppConfig, getServiceConfig, setFileSecret, setSecret, setVariable } from '../../services/AppConfigService';
import { restartService, sessionIdFor } from '../../services/SandboxService';
import { notifyAppConfigChanged } from '../../hooks/useAppConfigSignal';
import { errorText, formatBytes, nameProblem, when } from './appConfigModel';
import FileSecretForm from './FileSecretForm';
import './AppConfig.css';

/** After "Restart to apply": how often, and how many times, the service's state is read again (about a minute). */
export const RESTART_POLL_MS = 2000;
export const RESTART_POLL_TRIES = 30;

function Variables({ detail, canEdit, onSave, onDelete, busy }) {
  const [drafts, setDrafts] = useState({});
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [problem, setProblem] = useState(null);
  const add = async(event) => {
    event.preventDefault();
    const why = nameProblem(name, detail.reserved);
    if (why) { setProblem(why); return; }
    setProblem(null);
    if (await onSave(name.trim(), value)) { setName(''); setValue(''); }
  };
  return (
    <section aria-labelledby="cfg-vars-title" className="cfg-card" data-testid="cfg-variables">
      <div className="cfg-card-head">
        <h3 id="cfg-vars-title">Variables</h3>
        <p className="cfg-muted">Plain settings such as LOG_LEVEL or API_BASE_URL. Saved in <code>.flow/env.json</code>, part of the project&apos;s code. Read them with <code>process.env.NAME</code>.</p>
      </div>
      {detail.vars.length === 0 && <p className="cfg-empty">No variables yet.</p>}
      {detail.vars.length > 0 && (
        <DataTable
          actions={canEdit ? {
            label: 'Actions',
            width: 110,
            render: (v) => {
              const draft = drafts[v.name];
              const changed = draft !== undefined && draft !== v.value;
              return (
                <span className="cfg-actions">
                  <button className="cfg-btn is-sm is-primary" disabled={!changed || busy} onClick={async() => { if (await onSave(v.name, draft)) { setDrafts({ ...drafts, [v.name]: undefined }); } }} type="button">Save</button>
                  <IconButton className="cfg-btn is-sm is-quiet is-icon" disabled={busy} icon={<TrashIcon aria-hidden size={14} />} label={`Delete ${v.name}`} onClick={() => onDelete(v.name)} tip="Delete" />
                </span>
              );
            },
          } : undefined}
          className="cfg-table"
          columns={[
            { key: 'name', label: 'Name', width: 240, className: 'cfg-mono', value: (v) => v.name },
            {
              // A text box (not cut): editors change the value in place.
              key: 'value',
              label: 'Value',
              clip: false,
              value: (v) => (canEdit ? v.value : null),
              render: (v) => (canEdit
                ? <input aria-label={`Value of ${v.name}`} className="cfg-input cfg-mono" onChange={(e) => setDrafts({ ...drafts, [v.name]: e.target.value })} value={drafts[v.name] ?? v.value} />
                : <span className="cfg-muted" data-testid="cfg-var-hidden">Values are visible to editors</span>),
            },
          ]}
          rowKey="name"
          rowProps={(v) => ({ 'data-testid': `cfg-var-${v.name}` })}
          rows={detail.vars}
        />
      )}
      {canEdit && (
        <form className="cfg-add" data-testid="cfg-var-add" onSubmit={add}>
          <input aria-label="New variable name" className="cfg-input cfg-mono" onChange={(e) => setName(e.target.value.toUpperCase())} placeholder="NAME" value={name} />
          <input aria-label="New variable value" className="cfg-input cfg-mono cfg-grow" onChange={(e) => setValue(e.target.value)} placeholder="value" value={value} />
          <button className="cfg-btn is-sm" disabled={busy || !name} type="submit">Add variable</button>
          {problem && <p className="cfg-error" role="alert">{problem}</p>}
        </form>
      )}
    </section>
  );
}

/** A file secret's value cell: file name, size, who and when, and where the service finds it. */
function FileSecretValue({ secret }) {
  return (
    <span className="cfg-file-value">
      <span><span className="cfg-pill is-info is-xs">File</span> <span className="cfg-mono">{secret.fileName}</span>{secret.size != null && <span className="cfg-muted"> · {formatBytes(secret.size)}</span>}
        <span className="cfg-muted"> · set{secret.updatedAt ? ` ${when(secret.updatedAt)}` : ''}{secret.updatedBy ? ` by ${secret.updatedBy}` : ''}</span></span>
      <span className="cfg-muted cfg-small" data-testid={`cfg-file-path-${secret.name}`}>
        Its path is in <code>{secret.name}</code>{secret.envName && <> and <code>{secret.envName}</code></>}{secret.path && <> (<code>{secret.path}</code>)</>}{secret.mountPath && <>, also at <code>{secret.mountPath}</code></>}
      </span>
      {secret.provides && (
        <span className="cfg-muted cfg-small" data-testid={`cfg-file-provides-${secret.name}`}>
          Used for <code>{secret.provides}</code>
          {(secret.mountedAt || []).length > 0
            ? <>: mounted at {secret.mountedAt.map((m, i) => <React.Fragment key={`${m.service}:${m.target}`}>{i > 0 && ', '}<code>{m.target}</code> in {m.service} (compose)</React.Fragment>)}</>
            : <> (no compose file mounts it now)</>}
        </span>
      )}
    </span>
  );
}

FileSecretValue.propTypes = { secret: PropTypes.object.isRequired };

function Secrets({ detail, canEdit, onSave, onSaveFile, onDelete, busy }) {
  const [editing, setEditing] = useState(null); // a name, or '' for a new one
  const [kind, setKind] = useState('value'); // what a new or replaced secret is: 'value' | 'file'
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [problem, setProblem] = useState(null);
  const open = (n, k = 'value') => { setEditing(n); setKind(k); setName(n); setValue(''); setProblem(null); };
  const close = () => { setEditing(null); setKind('value'); setName(''); setValue(''); setProblem(null); };
  const save = async(event) => {
    event.preventDefault();
    const n = (editing || name).trim();
    const why = nameProblem(n, detail.reserved);
    if (why) { setProblem(why); return; }
    if (!value) { setProblem('Paste the secret value.'); return; }
    const ok = await onSave(n, value);
    // The value leaves the page once saved: it is never shown again.
    setValue('');
    if (ok) { close(); }
  };
  const editingRow = detail.secrets.find((s) => s.name === editing && s.kind === 'file') || null;
  const fileForm = (
    <FileSecretForm
      busy={busy}
      existing={editingRow || (editing ? { name: editing } : null)}
      maxBytes={detail.fileSecretMaxBytes}
      onCancel={close}
      onSave={onSaveFile}
      reserved={detail.reserved}
    />
  );
  const kindPicker = editing === '' && (
    <div aria-label="Kind of secret" className="cfg-kind" role="radiogroup">
      {[['value', 'Value'], ['file', 'File']].map(([k, label]) => (
        <button aria-checked={kind === k} className={`cfg-btn is-sm${kind === k ? ' is-on' : ' is-quiet'}`} key={k} onClick={() => setKind(k)} role="radio" type="button">{label}</button>
      ))}
      <span className="cfg-muted cfg-small">{kind === 'file' ? 'A file, e.g. a service account key or a certificate: the service gets its path.' : 'A text value, e.g. an API key: the service gets the value.'}</span>
    </div>
  );
  const form = kind === 'file' ? fileForm : (
    <form className="cfg-add" data-testid="cfg-secret-form" onSubmit={save}>
      {editing === '' && <input aria-label="Secret name" autoComplete="off" className="cfg-input cfg-mono" onChange={(e) => setName(e.target.value.toUpperCase())} placeholder="NAME" value={name} />}
      <input aria-label={editing ? `New value of ${editing}` : 'Secret value'} autoComplete="new-password" className="cfg-input cfg-mono cfg-grow" onChange={(e) => setValue(e.target.value)} placeholder="Paste the value" spellCheck={false} type="password" value={value} />
      <button className="cfg-btn is-sm is-primary" disabled={busy} type="submit">{editing ? 'Replace' : 'Save secret'}</button>
      <button className="cfg-btn is-sm is-quiet" onClick={close} type="button">Cancel</button>
      {problem && <p className="cfg-error" role="alert">{problem}</p>}
    </form>
  );
  return (
    <section aria-labelledby="cfg-secrets-title" className="cfg-card" data-testid="cfg-secrets">
      <div className="cfg-card-head">
        <h3 id="cfg-secrets-title">Secrets</h3>
        <p className="cfg-muted">API keys, passwords and tokens, or files such as a service account key. Stored encrypted in your sandbox, never in git and never shown again after saving: to change one, replace it. Read them with <code>process.env.NAME</code>, like variables; for a file, that is its path.</p>
      </div>
      {detail.secrets.length === 0 && <p className="cfg-empty">No secrets yet.</p>}
      {detail.secrets.length > 0 && (
        <DataTable
          actions={canEdit ? {
            label: 'Actions',
            width: 190,
            render: (s) => (
              <span className="cfg-actions">
                <button className="cfg-btn is-sm" disabled={busy} onClick={() => open(s.name, s.kind === 'file' ? 'file' : 'value')} type="button">{s.set ? 'Replace' : 'Set'}</button>
                {!s.set && <button className="cfg-btn is-sm is-quiet" disabled={busy} onClick={() => open(s.name, 'file')} type="button">Set a file</button>}
                <IconButton className="cfg-btn is-sm is-quiet is-icon" disabled={busy} icon={<TrashIcon aria-hidden size={14} />} label={`Delete ${s.name}`} onClick={() => onDelete(s.name)} tip="Delete" />
              </span>
            ),
          } : undefined}
          after={(s, { colCount }) => (editing === s.name ? <tr><td colSpan={colCount}>{form}</td></tr> : null)}
          className="cfg-table"
          columns={[
            { key: 'name', label: 'Name', width: 240, className: 'cfg-mono', value: (s) => s.name },
            {
              key: 'value',
              label: 'Value',
              value: (s) => (s.set ? s.updatedAt || 0 : null),
              title: (s) => (s.set && s.kind !== 'file' ? `set${s.updatedAt ? ` ${when(s.updatedAt)}` : ''}${s.updatedBy ? ` by ${s.updatedBy}` : ''}` : undefined),
              render: (s) => (s.set && s.kind === 'file' ? <FileSecretValue secret={s} /> : s.set
                ? <span className="cfg-masked"><span aria-hidden="true">••••••••</span> <span className="cfg-muted">set{s.updatedAt ? ` ${when(s.updatedAt)}` : ''}{s.updatedBy ? ` by ${s.updatedBy}` : ''}</span></span>
                : <span className="cfg-pill is-warn">Not set yet</span>),
            },
          ]}
          rowKey="name"
          rowProps={(s) => ({ 'data-testid': `cfg-secret-${s.name}` })}
          rows={detail.secrets}
        />
      )}
      {canEdit && editing === '' && <div className="cfg-new-secret">{kindPicker}{form}</div>}
      {canEdit && editing === null && <div><button className="cfg-btn is-sm" onClick={() => open('')} type="button">Add secret</button></div>}
    </section>
  );
}

export default function ConfigPanel({ projectId, focus = null, onOpenJobs = null }) {
  const [overview, setOverview] = useState(null);
  const [service, setService] = useState(focus?.service || null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const loadService = useCallback(async(name) => {
    if (!name) { setDetail(null); return; }
    const mine = ++seq.current;
    try {
      const next = await getServiceConfig(projectId, name);
      if (mine === seq.current) { setDetail(next); setError(null); }
      return next;
    } catch (e) {
      if (mine === seq.current) { setError(e); }
      return null;
    }
  }, [projectId]);

  const loadOverview = useCallback(async() => {
    try {
      const next = await getAppConfig(projectId);
      setOverview(next);
      setService((current) => current || next.services?.[0]?.name || null);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [projectId]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (focus?.service) { setService(focus.service); } }, [focus?.service]);
  useEffect(() => { loadService(service); }, [service, loadService]);

  const act = async(fn, done) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      if (done) { setNotice(done); }
      await loadService(service);
      loadOverview();
      notifyAppConfigChanged(projectId);
      return true;
    } catch (e) {
      setNotice({ tone: 'fail', text: errorText(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  // After a restart the runner takes a moment to start the service again: the state is read again every few seconds
  // until the service runs with its configuration (needsRestart false), for a short while at most.
  const poll = useRef(null);
  const stopPolling = useCallback(() => { clearTimeout(poll.current); poll.current = null; }, []);
  useEffect(() => stopPolling, [stopPolling, projectId, service]);
  const pollUntilApplied = (name, triesLeft = RESTART_POLL_TRIES) => {
    stopPolling();
    poll.current = setTimeout(async() => {
      const next = await loadService(name);
      // The "Restarting…" notice goes when the restart is over (applied, or no longer waited for); another notice stays.
      const clearRestarting = () => setNotice((current) => (current?.restarting ? null : current));
      if (next && !next.needsRestart) {
        poll.current = null;
        clearRestarting();
        loadOverview();
        notifyAppConfigChanged(projectId);
        return;
      }
      if (triesLeft > 1) { pollUntilApplied(name, triesLeft - 1); } else { poll.current = null; clearRestarting(); }
    }, RESTART_POLL_MS);
  };

  const restart = async() => {
    const name = service;
    const ok = await act(() => restartService(sessionIdFor(projectId, `services/${name}`)), { tone: 'pass', text: `Restarting ${name} with its new configuration.`, restarting: true });
    if (ok) { pollUntilApplied(name); }
  };

  if (error && !overview) {
    return (
      <div className="cfg-panel" data-testid="cfg-panel">
        <div className="cfg-state is-bad" role="alert"><strong>Configuration is not available</strong><p>{errorText(error)}</p></div>
      </div>
    );
  }
  if (!overview) { return <div className="cfg-panel" data-testid="cfg-panel"><p className="cfg-muted">Loading…</p></div>; }
  const canEdit = Boolean(detail ? detail.canEdit : overview.canEdit);

  return (
    <div className="cfg-panel" data-testid="cfg-panel">
      {/* The tab's title and what it is for ("Project / Ship / Configuration") are the workspace's (WorkspaceView). */}
      {!canEdit && <p className="cfg-muted">You are a viewer: you see the names, not the values.</p>}
      {overview.services.length === 0 && <div className="cfg-state"><strong>No services yet</strong><p>A service is a folder under services/. Create one first, then configure it here.</p></div>}
      {overview.services.length > 0 && (
        <nav aria-label="Services" className="cfg-services" role="tablist">
          {overview.services.map((s) => (
            <button aria-selected={s.name === service} className={`cfg-service${s.name === service ? ' is-on' : ''}`} data-testid={`cfg-service-${s.name}`} key={s.name} onClick={() => setService(s.name)} role="tab" type="button">
              <span className="cfg-mono">{s.name}</span>
              <span className="cfg-muted cfg-small">{s.vars} var · {s.secrets} secret</span>
              {s.secretsMissing?.length > 0 && <span className="cfg-pill is-warn is-xs" title={s.secretsMissing.join(', ')}>{s.secretsMissing.length} to set</span>}
              {s.needsRestart && <span className="cfg-pill is-prog is-xs">restart</span>}
            </button>
          ))}
        </nav>
      )}
      {notice && <p className={`cfg-banner is-${notice.tone}`} data-testid="cfg-notice" role="status">{notice.text}</p>}
      <p className="cfg-muted cfg-small" data-testid="cfg-jobs-moved">
        Scheduled jobs are in{' '}
        {onOpenJobs ? <button className="cfg-linkbtn" onClick={onOpenJobs} type="button">Operate › Jobs</button> : 'Operate › Jobs'}.
      </p>
      {error && overview && <p className="cfg-banner is-fail" role="alert">{errorText(error)}</p>}
      {detail && detail.service === service && (
        <>
          {detail.needsRestart && (
            <div className="cfg-banner is-prog cfg-restart" data-testid="cfg-restart" role="status">
              <span>{service} is running with its previous configuration.</span>
              {canEdit && <button className="cfg-btn is-sm is-primary" disabled={busy} onClick={restart} type="button">Restart to apply</button>}
            </div>
          )}
          {detail.errors?.length > 0 && <p className="cfg-banner is-warn">Some entries in the files were skipped: {detail.errors.join('; ')}</p>}
          <Variables
            busy={busy}
            canEdit={canEdit}
            detail={detail}
            onDelete={(name) => act(() => deleteVariable(projectId, service, name), { tone: 'pass', text: `${name} removed. Restart ${service} to apply.` })}
            onSave={(name, value) => act(() => setVariable(projectId, service, name, value), { tone: 'pass', text: `${name} saved. Restart ${service} to apply.` })}
          />
          <Secrets
            busy={busy}
            canEdit={canEdit}
            detail={detail}
            onDelete={(name) => act(() => deleteSecret(projectId, service, name), { tone: 'pass', text: `Secret ${name} removed.` })}
            onSave={(name, value) => act(() => setSecret(projectId, service, name, value), { tone: 'pass', text: `Secret ${name} saved. It will not be shown again. Restart ${service} to apply.` })}
            onSaveFile={(name, file) => act(() => setFileSecret(projectId, service, name, file), { tone: 'pass', text: `File secret ${name} saved. It will not be shown again. Restart ${service} to apply.` })}
          />
        </>
      )}
    </div>
  );
}

ConfigPanel.propTypes = {
  // `service`: open that one.
  focus: PropTypes.shape({ service: PropTypes.string }),
  // Opens Operate › Jobs (the scheduled jobs' own tab); without it the line names the tab only.
  onOpenJobs: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};
